import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRightIcon, CalendarIcon, CheckIcon } from 'lucide-react';
import { usePageMeta } from '../../hooks/usePageMeta';
import { PageHero } from '../../components/ui/PageHero';
import { Section } from '../../components/ui/Section';
import { ActionLink } from '../../components/ui/ActionLink';
import { TextLink } from '../../components/ui/TextLink';
import { FaqList } from '../../components/ui/FaqList';
import { EnquirySection } from '../../components/forms/EnquirySection';
import { PricePlanCard } from '../../components/pricing/PricePlanCard';
import { PriceTableCard } from '../../components/pricing/PriceTableCard';
import { NotFound } from '../NotFound';
import { priceLists } from '../../data/priceLists';
import { priceListLinks } from '../../data/navigation';
import type { FeeItem, PriceList } from '../../types/content';
export function PriceListPage() {
  const {
    slug
  } = useParams();
  const list = priceLists.find(item => item.slug === slug);
  usePageMeta(list?.metaTitle ?? 'Price list not found | ES World', list?.metaDescription ?? 'This page does not exist.');
  if (!list) return <NotFound data-id="element-1377" data-mp="3krbrv9kkldag" />;
  const hasPlans = Boolean(list.plans?.length);
  return <>
      <PageHero breadcrumbs={[{
      label: 'About Us',
      to: '/about/who-we-are'
    }, {
      label: `Price List ${list.name}`,
      to: `/price-list/${list.slug}`
    }]} title={list.h1} lead={list.lead} primary={{
      label: 'Request a full quote',
      to: '#enquire'
    }} secondary={{
      label: 'See other fees',
      to: '#fees'
    }} data-id="element-1378" data-mp="36vznsbkiyagv" />

      <Section id="prices" title="Tuition, week by week" description={hasPlans ? 'Pick your plan, then find your course length. The price shown is per week for your whole booking — the longer you study, the less you pay each week.' : `Weekly prices in ${list.currency}. All values marked 000 are samples.`} mockup={hasPlans ? undefined : 'Sample prices'} data-id="element-1379" data-mp="2n78og9us65uo">
        <PriceListSwitcher current={list.slug} data-id="element-1380" data-mp="opyh1apoq2xc" />
        {list.validity ? <p className="mb-8 inline-flex items-center gap-2 rounded-full bg-cream px-4 py-2 type-small font-semibold text-ink" data-id="element-1381" data-mp="jw2q2s7u9gbo">
            <CalendarIcon className="h-4 w-4 text-brand-press" aria-hidden="true" data-id="element-1382" data-mp="awph00nmmtrh" />
            {list.validity}
          </p> : null}

        {hasPlans ? <div className="grid gap-6 lg:grid-cols-2" data-id="element-1383" data-mp="17m6rcsxk13zi">
            {list.plans!.map(plan => <PricePlanCard key={plan.name} plan={plan} data-id="element-1384" data-mp="12oog2vw2ecoc" />)}
          </div> : <LegacyPriceTable list={list} data-id="element-1385" data-mp="ykgs50xfl0r6" />}

        <div className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center" data-id="element-1386" data-mp="tnqeek93mh8x">
          <ActionLink to="#enquire" data-id="element-1387" data-mp="3jd226uilkx4m">Get my personal quote</ActionLink>
          <TextLink to="/refund-policy" data-id="element-1388" data-mp="3ja6518ekqch8">Read our refund policy</TextLink>
        </div>
      </Section>

      {list.professional?.length ? <Section id="professional" bg="grey" title="Professional courses" description="Fixed course prices for teacher training and career courses." data-id="element-1389" data-mp="1tw6kd6xi0vgl">
          <FeeList items={list.professional} data-id="element-1390" data-mp="1ysdn6qullqub" />
        </Section> : null}

      {list.visas?.length ? <Section id="visas" title="Visa fees" description="ES World can sponsor your visa. Prices depend on whether you apply from outside or inside the UAE. Student insurance of 150 USD is mandatory for 6-month and 1-year residence visas." data-id="element-1391" data-mp="27ms38vmyd5eh">
          <div className="grid gap-6 lg:grid-cols-2" data-id="element-1392" data-mp="2hn2ielsjsmwa">
            {list.visas.map(visa => <article key={visa.title} className="rounded-[28px] bg-cream p-6 md:p-8" data-id="element-1393" data-mp="2mhyatn1p5c9x">
                <h3 className="type-h4 text-ink" data-id="element-1394" data-mp="2qkuyvta0a0l9">{visa.title}</h3>
                <div className="mt-6 space-y-6" data-id="element-1395" data-mp="s6t7ykyfp3vn">
                  {visa.groups.map(group => <div key={group.title} data-id="element-1396" data-mp="w6v5qj11td5n">
                      <p className="flex items-baseline justify-between gap-4" data-id="element-1397" data-mp="w9pnu3wlalvw">
                        <span className="type-card-title text-ink" data-id="element-1398" data-mp="vvjlkrr95os">{group.title}</span>
                        {group.intro ? <span className="type-small text-body" data-id="element-1399" data-mp="3t0zffchx703f">{group.intro}</span> : null}
                      </p>
                      <div className="mt-2 rounded-[18px] bg-white px-5" data-id="element-1400" data-mp="atjoh3j2rtko">
                        <FeeList items={group.items} compact data-id="element-1401" data-mp="5x2sjn5mwax1" />
                      </div>
                    </div>)}
                </div>
                {visa.footnote ? <p className="mt-5 type-small text-body" data-id="element-1402" data-mp="39e1haak0snhu">{visa.footnote}</p> : null}
              </article>)}
          </div>
        </Section> : null}

      {list.accommodation?.length ? <Section id="accommodation" bg="cream" title="Accommodation" description="Prices per person, per week. All accommodation is subject to availability — your ES advisor confirms your room." data-id="element-1403" data-mp="3jaehlra2lzlk">
          <div className="grid gap-4 md:grid-cols-2" data-id="element-1404" data-mp="2qlqurr9ob25w">
            {list.accommodation.map(table => <PriceTableCard key={table.title} table={table} data-id="element-1405" data-mp="163y36hzwb25s" />)}
          </div>
        </Section> : null}

      <Section id="fees" bg={list.accommodation?.length ? 'white' : 'cream'} title="Other fees" description="One-time fees and optional extras, shown clearly." mockup={hasPlans ? undefined : 'Sample fees'} data-id="element-1406" data-mp="1awr9hab0iijz">
        <div className="grid gap-12 lg:grid-cols-2" data-id="element-1407" data-mp="113k8e1nb1opp">
          <FeeList items={list.fees} data-id="element-1408" data-mp="1owagfzs37nup" />
          <div data-id="element-1409" data-mp="1ot4qkibmwhxx">
            <h3 className="type-h4 text-ink" data-id="element-1410" data-mp="1otee7z97id0g">Your tuition includes</h3>
            <ul className="mt-5 space-y-3" data-id="element-1411" data-mp="1ttj0k5wsq1yl">
              {list.included.map(item => <li key={item} className="flex gap-3 type-body text-ink" data-id="element-1412" data-mp="1fu14r6l31ofq">
                  <CheckIcon className="mt-1 h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-1413" data-mp="1kskttfuz3gcb" />
                  {item}
                </li>)}
            </ul>
          </div>
        </div>
      </Section>

      {list.notes?.length ? <Section id="good-to-know" bg="grey" title="Good to know" description="Key terms from our 2026 price list. Full details are in our terms and conditions." data-id="element-1414" data-mp="9ce3z90jx4um">
          <div className="grid gap-10 lg:grid-cols-12" data-id="element-1415" data-mp="265us7b4mpnoj">
            <ul className="space-y-4 lg:col-span-7" data-id="element-1416" data-mp="21dl7c9idfdt4">
              {list.notes.map(note => <li key={note} className="flex gap-3 type-body text-ink" data-id="element-1417" data-mp="1xchs9p85dlsy">
                  <ArrowRightIcon className="mt-1.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-1418" data-mp="1xa5d1jtrxpjl" />
                  {note}
                </li>)}
            </ul>
            {list.holidays ? <aside className="h-fit rounded-[22px] bg-white p-6 lg:col-span-5" data-id="element-1419" data-mp="1iez68umutmp7">
                <h3 className="type-card-title text-ink" data-id="element-1420" data-mp="1ih1vhcg6nbsv">School and public holidays 2026</h3>
                <p className="mt-3 type-card-desc text-body" data-id="element-1421" data-mp="1ef6xagtjtejb">{list.holidays}</p>
              </aside> : null}
          </div>
        </Section> : null}

      <Section id="faq" title="Price questions, answered" description="Short answers about prices and payments." data-id="element-1422" data-mp="38sg8qe5nna47">
        <FaqList items={list.faqs} data-id="element-1423" data-mp="uj47rvjdq6ev" />
      </Section>

      <EnquirySection bg="grey" title="Get a price for your dates" description="Tell us your course, your dates and any extras. We send you a personal quote by email." submitLabel="Send my price question" defaultLocation={list.slug === 'camps' ? '' : list.slug} defaultCourse={list.slug === 'camps' ? 'language-camps' : ''} data-id="element-1424" data-mp="zigv0he5xfhz" />
    </>;
}
function PriceListSwitcher({
  current
}: {
  current: string;
}) {
  return <nav aria-label="Other price lists" className="mb-6 flex flex-wrap gap-2" data-id="element-1425" data-mp="glyvvewzidbp">
      {priceListLinks.map(link => {
      const active = link.to === `/price-list/${current}`;
      return <Link key={link.to} to={link.to} aria-current={active ? 'page' : undefined} className={`rounded-full px-4 py-2 type-button font-bold transition-colors duration-150 ${active ? 'bg-brand text-white' : 'bg-mist text-ink hover:bg-line'}`} data-id="element-1426" data-mp="pig70i5cmb7g">
            {link.label}
          </Link>;
    })}
    </nav>;
}
function FeeList({
  items,
  compact
}: {
  items: FeeItem[];
  compact?: boolean;
}) {
  return <dl className={compact ? 'divide-y divide-line' : 'grid gap-x-12 sm:grid-cols-2 lg:grid-cols-1'} data-id="element-1427" data-mp="1lh0qxexi02gf">
      {items.map(fee => <div key={fee.label} className={`flex items-baseline justify-between gap-6 ${compact ? 'py-3' : 'border-b border-line py-4'}`} data-id="element-1428" data-mp="1gmwh8nwulgbn">
          <dt className="type-card-desc text-ink" data-id="element-1429" data-mp="1qfc1jrvmagzp">{fee.label}</dt>
          <dd className="shrink-0 type-h5 text-ink" data-id="element-1430" data-mp="1qgwyfo5emc0q">{fee.price}</dd>
        </div>)}
    </dl>;
}
function LegacyPriceTable({
  list
}: {
  list: PriceList;
}) {
  return <div className="overflow-x-auto rounded-2xl bg-white shadow-card" data-id="element-1431" data-mp="20dhzjyeaz2za">
      <table className="w-full min-w-[720px] text-left" data-id="element-1432" data-mp="a1c6ovbmphjb">
        <caption className="sr-only" data-id="element-1433" data-mp="5yp9sg03p6f9">{list.h1}</caption>
        <thead data-id="element-1434" data-mp="60a3k3elclc2">
          <tr className="border-b border-line type-small text-muted" data-id="element-1435" data-mp="60s7znbeq5v2">
            <th scope="col" className="px-6 py-4 font-semibold" data-id="element-1436" data-mp="3s1g0g0rmskab">Course</th>
            <th scope="col" className="px-6 py-4 font-semibold" data-id="element-1437" data-mp="3w1zyd6k13rxi">Lessons</th>
            {list.tierLabels.map(label => <th key={label} scope="col" className="px-6 py-4 text-right font-semibold" data-id="element-1438" data-mp="3i9k4av93v48q">
                {label}
              </th>)}
          </tr>
        </thead>
        <tbody data-id="element-1439" data-mp="38bw6txk47imv">
          {list.rows.map(row => <tr key={row.course} className="border-b border-line last:border-0" data-id="element-1440" data-mp="38e9xlu7ri8om">
              <th scope="row" className="px-6 py-4 font-semibold text-ink" data-id="element-1441" data-mp="3dcjx2h0irvg1">{row.course}</th>
              <td className="px-6 py-4" data-id="element-1442" data-mp="2zd24dqk3ry1e">{row.lessons}</td>
              {row.prices.map((price, index) => <td key={`${row.course}-${index}`} className="px-6 py-4 text-right type-h5 text-ink" data-id="element-1443" data-mp="34au4v8ytcsh7" data-mpi={index}>
                  {price}
                </td>)}
            </tr>)}
        </tbody>
      </table>
    </div>;
}